import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Users, 
  DollarSign, 
  TrendingUp, 
  Image,
  Calendar,
  ArrowUpRight,
  Activity
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";
import SystemHealthDashboard from "../../components/admin/SystemHealthDashboard";

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [analyticsRes, billingRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview`, { headers }),
        axios.get(`${API}/admin/billing/overview`, { headers })
      ]);
      
      setAnalytics(analyticsRes.data);
      setBilling(billingRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const statCards = [
    {
      title: "Monthly Recurring Revenue",
      value: `$${billing?.mrr?.toFixed(2) || "0.00"}`,
      icon: <DollarSign className="w-5 h-5" />,
      color: "text-green-400",
      bgColor: "bg-green-400/10",
      change: "+12%"
    },
    {
      title: "Active Photographers",
      value: analytics?.photographers?.active || 0,
      icon: <Users className="w-5 h-5" />,
      color: "text-blue-400",
      bgColor: "bg-blue-400/10",
      subtext: `${analytics?.photographers?.trialing || 0} trialing`
    },
    {
      title: "Total Bookings",
      value: analytics?.bookings?.total || 0,
      icon: <Calendar className="w-5 h-5" />,
      color: "text-purple-400",
      bgColor: "bg-purple-400/10",
      subtext: `${analytics?.bookings?.pending || 0} pending`
    },
    {
      title: "Total Galleries",
      value: analytics?.galleries?.total || 0,
      icon: <Image className="w-5 h-5" />,
      color: "text-orange-400",
      bgColor: "bg-orange-400/10",
      subtext: `${analytics?.galleries?.public || 0} public`
    }
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Admin Dashboard</h1>
        <p className="text-muted-foreground">Platform overview and key metrics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="bg-card border-border card-hover">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-sm ${stat.bgColor}`}>
                    <span className={stat.color}>{stat.icon}</span>
                  </div>
                  {stat.change && (
                    <span className="text-xs text-green-400 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {stat.change}
                    </span>
                  )}
                </div>
                <h3 className="text-3xl font-serif mb-1">{stat.value}</h3>
                <p className="text-sm text-muted-foreground">{stat.title}</p>
                {stat.subtext && (
                  <p className="text-xs text-muted-foreground mt-1">{stat.subtext}</p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Revenue by Plan + System Health */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-serif">Revenue by Plan</CardTitle>
            <Link to="/admin/billing">
              <Button variant="ghost" size="sm">
                View All <ArrowUpRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(billing?.plan_breakdown || {}).map(([key, data]) => (
                <div key={key} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${
                      key === "starter" ? "bg-blue-400" :
                      key === "pro" ? "bg-purple-400" : "bg-orange-400"
                    }`} />
                    <span className="capitalize">{key}</span>
                    <span className="text-muted-foreground text-sm">
                      ({data.count} subscribers)
                    </span>
                  </div>
                  <span className="font-medium">${data.revenue.toFixed(2)}/mo</span>
                </div>
              ))}
            </div>
            
            <div className="mt-6 pt-6 border-t border-border">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Churn Rate</span>
                <span className={`font-medium ${
                  (billing?.subscriptions?.churn_rate || 0) > 10 ? "text-red-400" : "text-green-400"
                }`}>
                  {billing?.subscriptions?.churn_rate?.toFixed(1) || 0}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-serif">Platform Metrics</CardTitle>
            <Activity className="w-5 h-5 text-green-400" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Trial Conversion Rate</span>
                <span className="font-medium">
                  {analytics?.photographers?.trial_conversion_rate?.toFixed(1) || 0}%
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Active Subscriptions</span>
                <span className="font-medium text-green-400">
                  {billing?.subscriptions?.active || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Canceled Subscriptions</span>
                <span className="font-medium text-red-400">
                  {billing?.subscriptions?.canceled || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total Storage Used</span>
                <span className="font-medium">
                  {analytics?.media?.total_storage_mb?.toFixed(1) || 0} MB
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total Media Assets</span>
                <span className="font-medium">
                  {analytics?.media?.total_assets || 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Health Dashboard - Full Width */}
      <SystemHealthDashboard />

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-3 gap-6">
        <Link to="/admin/photographers">
          <Card className="bg-card border-border card-hover h-full">
            <CardContent className="p-6">
              <Users className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-lg font-medium mb-2">Manage Photographers</h3>
              <p className="text-sm text-muted-foreground">
                View accounts, change plans, suspend users
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/billing">
          <Card className="bg-card border-border card-hover h-full">
            <CardContent className="p-6">
              <DollarSign className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-lg font-medium mb-2">View Transactions</h3>
              <p className="text-sm text-muted-foreground">
                Review payments, process refunds
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/analytics">
          <Card className="bg-card border-border card-hover h-full">
            <CardContent className="p-6">
              <TrendingUp className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-lg font-medium mb-2">View Analytics</h3>
              <p className="text-sm text-muted-foreground">
                Trends, client activity, growth metrics
              </p>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
